<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sgoto, spath } from 'hydria/svelte_helpers'

    import { makeCompareContact, sortContacts } from 'wdom/services/contacts'
    import type { IContact } from 'wdom/services/contacts'
    import { Breadcrumb } from 'breadcrumb'

    import type { User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import ContactGroupEditor from 'wdom/components/contacts/ContactGroupEditor.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Province from 'wdom/components/contacts/Province.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const userId = getContext('userId') as Readable<string>
    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const addresses = sderived(userh.addresses, ($addresses) => $addresses)

    const tldValidity: Writable<{ [key: string]: boolean }> = writable({})

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    let inlineModal: boolean = $state(true)

    let domainStatus: 'loading' | 'error' | 'importing' | 'ready' = $state('loading')

    const crumbs = sderived(status, ($status) => [
        { txt: $_('Users'), url: spath('/wdom-users') },
        {
            txt: $status == 'ready' ? $user.email : $status == 'loading' ? '…' : '<Icon inline icon="fa:exclamation-triangle" />',
            html: $status != 'ready' && $status != 'loading',
            url: spath(`/wdom-user/${$userId}`),
        },
        { txt: $_('Importation d’un domaine'), url: spath(`/wdom-user/${$userId}/import`) },
        { txt: $_('Configuration') },
    ])

    interface ExtraDomainData {
        expiration_string?: string
        status?: string
        stage_of_life?: string
        webnames_capabilities?: Set<string>
    }
    let extraDomainData: ExtraDomainData = $state({})

    // These are the contacts we send to the editor as the "currently saved".
    let contacts: IContact[] = $state([])
    let originalContacts: IContact[] = $state([])
    let originalContactsForComparison: IContact[] | null = $derived(originalContacts == null ? null : originalContacts.map((c) => makeCompareContact(c)))

    // This acts as a signal to the group editor: set the edited value to this
    let setContacts: IContact[] = $state([])

    // This is what the editor updates when there are changes
    const contactsEdited: Writable<IContact[]> = writable([])

    async function loadLiveDomainContacts(idna: string) {
        domainStatus = 'loading'

        try {
            const r = await simpleFetch('GET', `/json/domain/1/live-domain-contacts/${route.result.path.params?.system}/${domainIdna}`)
            extraDomainData = {
                expiration_string: r.data.expiration_srting,
            }
            switch (route.result.path.params?.system) {
                case 'cira':
                    Object.assign(extraDomainData, {
                        status: r.data.cira_status.join(','),
                        stage_of_life: r.data.stage_of_life,
                    })
                    break
                case 'webnames':
                    Object.assign(extraDomainData, {
                        status: r.data.webnames_status.join(','),
                        webnames_capabilities: new Set(r.data.webnames_capabilities),
                    })
                    break
            }

            if (r.data.contacts == null) {
                r.data.contacts = []
            }
            for (const c of r.data.contacts) {
                if (c.type == 'owner') c.type = 'registrant'
            }
            originalContacts = sortContacts(r.data.contacts)
            contacts = cloneDeep(originalContacts)

            domainStatus = 'ready'
        } catch (e) {
            console.error('Error loading domain contacts:', e)
            notify({
                type: 'danger',
                title: 'Importation',
                text: 'L’information sur le domaine n’a pas pu être chargée de l’autorité.',
            })
            domainStatus = 'error'
        }
    }

    interface IShortDomain {
        idna: string
        name: string
    }
    let shortDomainList: IShortDomain[] = $state([])

    async function loadShortDomainList() {
        try {
            const r = await simpleFetch('GET', `/json/domain/1/contact-domain-list/${$userId}`)
            shortDomainList = r.data.domains
        } catch (e) {
            console.error('Failed to load short domain list:', e)
            notify({
                type: 'danger',
                title: 'Domaines alternatifs',
                text: 'La liste de domaines du client n’a pas pu être chargée.',
            })
        }
    }

    let showShortDomainSelection = $state(false)

    let shortDomainSelectionTypes = $state({
        registrant: true,
        admin: true,
        billing: true,
        tech: true,
    })
    const contactTexts = {
        registrant: 'Titulaire',
        admin: 'Administratif',
        billing: 'Facturation',
        tech: 'Technique(s)',
    }

    function openShortSelection() {
        for (const k of Object.keys(shortDomainSelectionTypes)) {
            shortDomainSelectionTypes[k] = true
        }
        showShortDomainSelection = true
    }

    async function importShortDomain(sdomain: IShortDomain) {
        showShortDomainSelection = false

        try {
            const r = await simpleFetch('GET', `/json/domain/1/${sdomain.idna}`, { with_contacts: '1' })
            const lcontacts: IContact[] = []
            for (const c of r.data.contacts) {
                if (c.type == 'owner') c.type = 'registrant'
                if (shortDomainSelectionTypes[c.type]) lcontacts.push(c)
                else {
                    for (const ocontact of contacts) {
                        if (ocontact.type == c.type) lcontacts.push(ocontact)
                    }
                }
            }
            setContacts = sortContacts(lcontacts)
        } catch {
            notify({
                type: 'danger',
                title: $_('Contacts'),
                text: $_('Failed to load contacts from “{domain}”', { domain: sdomain.name }),
            })
            return
        }
    }

    function resetToSaved() {
        setContacts = cloneDeep(originalContacts)
    }

    async function importDomain() {
        domainStatus = 'importing'

        const data = {
            owner_id: $userId,
            system: route.result.path.params?.system,
            domain_name: domainIdna,
            contacts: null as any,
        }

        if (dirty && (route.result.path.params?.system != 'webnames' || (extraDomainData.webnames_capabilities && extraDomainData.webnames_capabilities.has('contacts')))) {
            data.contacts = cloneDeep($contactsEdited)
        }

        try {
            const r = await simpleFetch('POST', '/json/domain/1/import-domain', data)
            notify({
                type: 'success',
                title: 'Importation d’un domaine',
                text: `L’importation du domaine « ${domainUnicode} » est lancée.`,
            })
            sgoto(`/wdom-user/${$userId}`)
        } catch (e: any) {
            console.error('Error importing:', e)
            notify({
                type: 'danger',
                title: 'Importation d’un domaine',
                text: `L’importation du domaine « ${domainUnicode} » a généré une erreur${e && e.data && e.data.error ? ': “' + e.data.error + '”' : ''}.`,
            })

            domainStatus = 'ready'
        }
    }

    onDestroy(() => {
        if (showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    let domainIdna = $derived(route.result.path.params?.domainIdna.toLowerCase())
    let domainUnicode = $derived(toUnicode(domainIdna))
    let contactsEditedForComparison = $derived($contactsEdited == null ? null : $contactsEdited.map((c) => makeCompareContact(c)))
    let dirty = $derived(originalContactsForComparison != null && contactsEditedForComparison != null && !isEqual(contactsEditedForComparison, originalContactsForComparison))
    let allValid = $derived(Object.entries($tldValidity).filter(([k, v]) => k != 'contextual' && v === false).length == 0)

    let lastPrep: string | undefined
    $effect(() => {
        if (domainIdna && domainIdna != lastPrep) {
            lastPrep = domainIdna
            loadLiveDomainContacts(domainIdna)
            loadShortDomainList()
        }
    })
    $effect(() => {
        if (showShortDomainSelection && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .contact-import-related .card-body > div {
        display: flex;
        gap: 0.5rem;
    }
    .contact-import-related .card-body > div > div {
        flex: 1 0 0;
    }
    #emeta-sb-user .card-body > div {
        display: grid;
        gap: 1rem;
        grid-template-columns: 2fr 1fr;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
                showShortDomainSelection = false
            }
        }
    }}
/>

<Breadcrumb {crumbs} />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else if domainStatus == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        <p>L’information sur le domaine n’a pas pu être chargée de l’autorité.</p>
    </div>
{:else}
    <div class="d-grid gap-4">
        <div class="contact-import-related">
            <div class="card">
                <div class="card-title text-primary">{$_('Domain')}</div>
                <div class="card-body">
                    <div>
                        <strong>Domaine :</strong>
                        <div>
                            {domainUnicode}
                        </div>
                    </div>
                    {#if domainIdna !== domainUnicode}
                        <div>
                            <strong>IDNA :</strong>
                            <div>
                                {domainIdna}
                            </div>
                        </div>
                    {/if}
                    <div>
                        <strong>Système :</strong>
                        <div>
                            {route.result.path.params?.system}
                        </div>
                    </div>
                    <div>
                        <strong>Stage of life :</strong>
                        <div>
                            {#if domainStatus == 'loading'}<LoadingPattern pattern={['125px']} />{:else}{extraDomainData.stage_of_life ? extraDomainData.stage_of_life : '–'}{/if}
                        </div>
                    </div>
                    <div>
                        <strong>Status :</strong>
                        <div>
                            {#if domainStatus == 'loading'}<LoadingPattern pattern={['75px']} />{:else}{extraDomainData.status ? extraDomainData.status : '–'}{/if}
                        </div>
                    </div>
                </div>
            </div>
            <div class="card" id="emeta-sb-user">
                <div class="card-title text-primary">{$_('User')}</div>
                <div class="card-body">
                    <div>
                        <dl>
                            <dt>{$_('Full name')}</dt>
                            {#if $loading}
                                <dd><LoadingPattern pattern={['125px']} /></dd>
                            {:else}
                                <dd>{$addresses[0].name ? $addresses[0].name : '–'}</dd>
                            {/if}
                            <dt>{$_('Organization')}</dt>
                            {#if $loading}
                                <dd><LoadingPattern pattern={['125px']} /></dd>
                            {:else}
                                <dd>{$addresses[0].org ? $addresses[0].org : '–'}</dd>
                            {/if}
                            <dt>{$_('Address')}</dt>
                            {#if $loading}
                                <dd><LoadingPattern pattern={['225px']} /></dd>
                                <dd><LoadingPattern pattern={['125px']} /></dd>
                                <dd><LoadingPattern pattern={['125px']} /></dd>
                            {:else}
                                {#if $addresses[0].street && $addresses[0].street.length}
                                    {#each $addresses[0].street as street}
                                        <dd>{street}</dd>
                                    {/each}
                                {:else}
                                    <dd>–</dd>
                                {/if}
                                <dd>{$addresses[0].city ? $addresses[0].city : '<city>'}, <Province contact={$addresses[0]} /></dd>
                                <dd><PostalCode contact={$addresses[0]} />, <Country contact={$addresses[0]} /></dd>
                            {/if}
                        </dl>
                        <dl>
                            {#if $loading || $addresses[0].voice}
                                <dt>{$_('Voice')}</dt>
                                {#if $loading}
                                    <dd><LoadingPattern pattern={['95px']} /></dd>
                                {:else}
                                    <dd><InternationalPhone phone={$addresses[0].voice} /></dd>
                                {/if}
                            {/if}
                            {#if $loading || $addresses[0].mobile}
                                <dt>{$_('Mobile')}</dt>
                                {#if $loading}
                                    <dd><LoadingPattern pattern={['95px']} /></dd>
                                {:else}
                                    <dd><InternationalPhone phone={$addresses[0].mobile} /></dd>
                                {/if}
                            {/if}
                            {#if $loading || $addresses[0].fax}
                                <dt>{$_('FAX')}</dt>
                                {#if $loading}
                                    <dd><LoadingPattern pattern={['95px']} /></dd>
                                {:else}
                                    <dd><InternationalPhone phone={$addresses[0].fax} /></dd>
                                {/if}
                            {/if}
                        </dl>
                    </div>
                </div>
            </div>
        </div>
        {#if domainStatus == 'loading'}
            <div class="card card-primary">
                <div class="card-body">
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                    <div style="text-align: center; padding: 1em;">Un moment…</div>
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                </div>
            </div>
        {:else if domainStatus == 'importing'}
            <div class="card card-warning">
                <div class="card-title text-primary">Importation</div>
                <div class="card-body">
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                    <div style="text-align: center; padding: 1em;" class="text-warning">Préparation de l’importation…</div>
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                </div>
            </div>
        {:else if domainStatus == 'ready'}
            <div id="container-domain" class:has-contact-card={inlineModal}>
                <div class="card card-contacts card-clean">
                    <div class="card-title text-primary contact-title">
                        <div class="contacts-title">{$_('Contacts')}</div>
                        <div class="actions domain-contact-actions">
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-outline-secondary" class:active={!inlineModal} onclick={() => (inlineModal = !inlineModal)}><Icon inline icon="fa:external-link" /></button>
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" onclick={() => openShortSelection()} disabled={!shortDomainList || !shortDomainList.length}>
                                    <Icon inline icon="fa:globe" /><span class="hidden-xs hidden-sm">&nbsp;&nbsp;{$_('Import from another domain')}</span>
                                </button>
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" onclick={() => resetToSaved()} disabled={!dirty}>
                                    <Icon inline icon="fa:undo" /><span class="hidden-xs">&nbsp;&nbsp;{$_('Undo changes')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        {#if route.result.path.params?.system != 'webnames' || (extraDomainData.webnames_capabilities && extraDomainData.webnames_capabilities.has('contacts'))}
                            <ContactGroupEditor context="domain" {contacts} bind:setContacts {contactsEdited} updating={domainStatus != 'ready'} domains={[domainUnicode]} canEditRegistrant={true} inlineMode={inlineModal} {tldValidity} />
                        {/if}
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-title text-primary">Importer</div>
                <div class="card-body">
                    {#if route.result.path.params?.system == 'webnames' && (!extraDomainData.webnames_capabilities || !extraDomainData.webnames_capabilities.has('contacts'))}
                        <p>Les contacts sont partagés avec d’autres domaines.</p>
                        <button class="btn btn-primary" type="button" onclick={() => importDomain()}>Importer sans les contacts</button>
                    {:else}
                        <button class="btn btn-primary" type="button" disabled={!allValid} onclick={() => importDomain()}>
                            {#if !allValid}
                                Les contacts sont invalides
                            {:else if dirty}
                                Importer avec de nouveaux contacts
                            {:else}
                                Importer avec les contacts originaux
                            {/if}
                        </button>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
{/if}

{#if showShortDomainSelection}
    <div class="modal modal-contact-alt-domain" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Load contacts from another domain')}</div>
                </div>
                <div class="modal-body">
                    <div class="alt-domain-contact-selection">
                        {#each shortDomainList as sdomain}
                            <div>
                                <a
                                    href="/"
                                    onclick={(e) => {
                                        e.preventDefault()
                                        importShortDomain(sdomain)
                                    }}>{sdomain.name}</a
                                >
                            </div>
                        {/each}
                    </div>
                </div>
                <div class="modal-footer">
                    {#each Object.entries(shortDomainSelectionTypes) as [ttype, tval]}
                        <label for="short-c-{ttype}" style="white-space: nowrap; margin-right: 0.5em">
                            <input type="checkbox" id="short-c-{ttype}" bind:checked={shortDomainSelectionTypes[ttype]} />
                            {contactTexts[ttype]}
                        </label>
                    {/each}
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (showShortDomainSelection = false)}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (showShortDomainSelection = false)} aria-hidden="true"></div>
    </div>
{/if}
